/**
 * Connected-contracts bar.
 *
 * A thin horizontal strip rendered above the editor tabs that surfaces
 * the "kopplade avtal" relationship without stealing space from the
 * field column:
 *
 *   - When THIS contract is a child, a single breadcrumb-style chip
 *     points back at the parent ("↑ Tillhör: <parent title>").
 *   - When THIS contract is a root with existing connected children,
 *     each child renders as a compact status-dot chip linking to it.
 *   - When THIS contract is a root with matching connected templates,
 *     a "+ Lägg till kopplat avtal" button opens a popover listing the
 *     creatable templates (with descriptions inside the popover, not on
 *     the page itself).
 *
 * Connected contracts are independent contracts with their own
 * lifecycle, so creation is not gated by the parent's draft state. That
 * intent is also surfaced inside the popover as a one-line hint.
 *
 * Hidden entirely when none of the three roles apply (most templates).
 */

import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getTemplateVersion } from '@hyranai/regta-templates';
import { LegacyCreateLink, contractEditorPath, defaultEngine } from './DocumentEngine.js';
import {
  tenantApi,
  type ConnectedContractSummary,
  type DocumentEngine,
  type TemplateListRow,
} from '../api.js';
import { useTenantQuery } from '../query.js';

const STATUS_LABEL: Record<ConnectedContractSummary['status'], string> = {
  draft: 'Utkast',
  pending: 'Klart att skicka',
  in_review: 'Granskning',
  awaiting_manual_signature: 'Väntar på underskrift',
  sent: 'Skickat',
  viewed: 'Visat',
  awaiting_signature: 'Väntar på signering',
  signed: 'Signerat',
  archived: 'Arkiverat',
  cancelled: 'Avbrutet',
};

const STATUS_DOT: Record<ConnectedContractSummary['status'], string> = {
  draft: 'bg-stone-400',
  pending: 'bg-blue-500',
  in_review: 'bg-purple-500',
  awaiting_manual_signature: 'bg-indigo-500',
  sent: 'bg-indigo-500',
  viewed: 'bg-indigo-500',
  awaiting_signature: 'bg-indigo-500',
  signed: 'bg-green-500',
  archived: 'bg-stone-300',
  cancelled: 'bg-red-500',
};

type Props = {
  token: string;
  /** This contract's own ID. */
  contractId: string;
  /** This contract's templateKey — drives which templates can attach. */
  templateKey: string;
  /** Null if this contract is a root. */
  parentContractId: string | null;
  parent: ConnectedContractSummary | null;
  children: ConnectedContractSummary[];
  /** Locked write mode when contract is no longer draft. Today, only
   *  gates the "+ Lägg till" affordance — children remain visible. */
  disabled: boolean;
};

/**
 * Connected templates this contract can spawn as children: the registered
 * templates whose `connectedTo` lists this contract's `templateKey`. Empty
 * for a child (no grandchildren) or a template with no connected types.
 * Shared by the inline bar and the "Kopplade avtal" tab.
 */
export function useCreatableConnectedTemplates(
  templateKey: string,
  isRoot: boolean,
): TemplateListRow[] {
  const templatesQuery = useTenantQuery(
    'templates.list',
    () => tenantApi.templates.list(),
    [],
  );
  return useMemo<TemplateListRow[]>(() => {
    if (!isRoot) return [];
    const rows = templatesQuery.data?.templates ?? [];
    return rows.filter((r) => {
      const mod = getTemplateVersion(r.template_key, r.version);
      return mod?.metadata.connectedTo?.includes(templateKey) ?? false;
    });
  }, [templatesQuery.data, templateKey, isRoot]);
}

export function ConnectedContractsBar({
  token,
  contractId,
  templateKey,
  parentContractId,
  parent,
  children,
  disabled,
}: Props): ReactElement | null {
  const creatableTemplates = useCreatableConnectedTemplates(
    templateKey,
    parentContractId === null,
  );

  const isChild = parent !== null;
  const hasChildren = children.length > 0;
  const showCreate = !disabled && !isChild && creatableTemplates.length > 0;

  if (!isChild && !hasChildren && !showCreate) return null;

  // Child view — single breadcrumb chip back to the parent.
  if (isChild && parent) {
    return (
      <div className="mb-3 flex items-center gap-2 text-xs">
        <span className="tracking-wider text-stone-500 uppercase text-[10px]">
          Tillhör
        </span>
        <Link
          to={`/contracts/${parent.id}/edit`}
          className="group inline-flex items-center gap-1.5 rounded-full border border-stone-300 bg-white px-2.5 py-1 font-medium text-stone-800 transition-colors hover:border-stone-500 hover:bg-stone-50"
          title={`Gå till överordnat avtal — ${STATUS_LABEL[parent.status] ?? parent.status}`}
        >
          <span aria-hidden="true" className="text-stone-400 group-hover:text-stone-600">
            ↑
          </span>
          <span className="max-w-[40ch] truncate">{parent.title}</span>
          <span aria-hidden="true" className="text-stone-300">·</span>
          <span className="text-stone-500">{STATUS_LABEL[parent.status] ?? parent.status}</span>
        </Link>
      </div>
    );
  }

  // Parent view — a management card listing each connected child with its
  // status and contextual actions (start review / manage signing), plus
  // the add-connected affordance. Clearer than the old chip row: the
  // relationship + each child's lifecycle is legible at a glance, and the
  // child's signing/review can be driven from here (the parent view) per
  // the LH-15 resolution.
  return (
    <div className="mb-3 rounded-md border border-stone-200 bg-stone-50/60 p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="tracking-wider text-stone-500 uppercase text-[10px]">
          Kopplade avtal{hasChildren ? ` (${children.length})` : ''}
        </span>
        {showCreate && (
          <AddConnectedPopover
            token={token}
            contractId={contractId}
            templates={creatableTemplates}
            label={addButtonLabel(creatableTemplates, hasChildren)}
          />
        )}
      </div>

      {hasChildren ? (
        <ul className="flex flex-col gap-1.5">
          {children.map((c) => (
            <ConnectedChildRow key={c.id} child={c} />
          ))}
        </ul>
      ) : (
        <p className="text-[11px] leading-relaxed text-stone-500">
          Inga kopplade avtal ännu. Ett kopplat avtal (t.ex. en
          överenskommelse om avstående från besittningsskydd) är ett eget
          avtal med egen granskning och signering.
        </p>
      )}
    </div>
  );
}

/** Statuses where signing has been initiated — the "Hantera signering"
 *  shortcut is most useful here (and on `pending`, to start it). */
const SIGNING_PHASE_STATUSES = new Set<ConnectedContractSummary['status']>([
  'awaiting_manual_signature',
  'sent',
  'viewed',
  'awaiting_signature',
]);

/**
 * One connected-child row: status dot + title + status label, with
 * contextual links to drive the child's review and signing from the
 * parent view.
 */
function ConnectedChildRow({ child: c }: { child: ConnectedContractSummary }) {
  const showReview = c.status === 'pending';
  // Only deep-link to the Signering tab once signing is actually in flight
  // — on `pending` that tab isn't reachable yet (it needs an explicit
  // "Fortsätt till signering" on the child first), so the link would
  // silently land on Avtal instead.
  const showSigning = SIGNING_PHASE_STATUSES.has(c.status);
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded border border-stone-200 bg-white px-2.5 py-1.5">
      <Link
        to={`/contracts/${c.id}/edit`}
        className="group flex min-w-0 flex-1 items-center gap-2"
      >
        <span
          aria-hidden="true"
          className={'h-1.5 w-1.5 shrink-0 rounded-full ' + STATUS_DOT[c.status]}
        />
        <span className="min-w-0 truncate text-xs font-medium text-stone-800 group-hover:underline">
          {c.title}
        </span>
        <span className="shrink-0 text-[10px] tracking-wide text-stone-400 uppercase">
          {STATUS_LABEL[c.status] ?? c.status}
        </span>
      </Link>
      <div className="flex shrink-0 items-center gap-3 text-[11px]">
        {showReview && (
          <Link
            to={`/contracts/${c.id}/edit`}
            className="font-medium text-purple-700 hover:underline"
            title="Öppna det kopplade avtalet och skicka det för granskning"
          >
            Påbörja granskning →
          </Link>
        )}
        {c.status === 'in_review' && (
          <span className="text-purple-700">Under granskning</span>
        )}
        {showSigning && (
          <Link
            to={`/contracts/${c.id}/edit?tab=signering`}
            className="font-medium text-indigo-700 hover:underline"
          >
            Hantera signering →
          </Link>
        )}
        {c.status === 'signed' && (
          <span className="text-emerald-700">Signerat ✓</span>
        )}
      </div>
    </li>
  );
}

/** Legacy alias — old left-rail name kept for any stragglers. */
export { ConnectedContractsBar as ConnectedContractsPanel };

/**
 * Full-tab connected-contracts management. Same child list + row actions
 * as the inline bar, but the "+ create" affordance is always available
 * here — so connected avtal can still be created after the parent leaves
 * draft (when the inline bar hides its create button). Surfaced via the
 * editor's "Kopplade avtal" tab.
 */
export function ConnectedContractsTab({
  token,
  contractId,
  templateKey,
  children,
}: {
  token: string;
  contractId: string;
  templateKey: string;
  children: ConnectedContractSummary[];
}): ReactElement {
  const creatable = useCreatableConnectedTemplates(templateKey, true);
  const hasChildren = children.length > 0;
  return (
    <div className="mx-auto w-full max-w-2xl p-6">
      <div className="mb-3 flex items-center justify-between gap-2 border-b border-stone-200 pb-2">
        <span className="text-xs font-medium tracking-wider text-stone-500 uppercase">
          Kopplade avtal{hasChildren ? ` (${children.length})` : ''}
        </span>
        {creatable.length > 0 && (
          <AddConnectedPopover
            token={token}
            contractId={contractId}
            templates={creatable}
            label={addButtonLabel(creatable, hasChildren)}
          />
        )}
      </div>
      {hasChildren ? (
        <ul className="flex flex-col gap-1.5">
          {children.map((c) => (
            <ConnectedChildRow key={c.id} child={c} />
          ))}
        </ul>
      ) : (
        <p className="text-xs leading-relaxed text-stone-500">
          Inga kopplade avtal ännu. Ett kopplat avtal (t.ex. en
          överenskommelse om avstående från besittningsskydd) är ett eget avtal
          med egen granskning och signering.
        </p>
      )}
    </div>
  );
}

/**
 * Surface the connected-template's type name on the add button when
 * there's exactly one creatable template — much more legible than the
 * generic "Lägg till kopplat avtal". With multiple templates the type
 * name is ambiguous, so we keep the generic copy and let the popover
 * disambiguate.
 */
function addButtonLabel(templates: TemplateListRow[], dense: boolean): string {
  if (templates.length === 1) {
    const only = templates[0]!;
    const mod = getTemplateVersion(only.template_key, only.version);
    const shortTitle = mod?.metadata.shortTitle ?? only.name;
    return shortTitle;
  }
  return dense ? 'Lägg till' : 'Lägg till kopplat avtal';
}

function AddConnectedPopover({
  token,
  contractId,
  templates,
  label,
}: {
  token: string;
  contractId: string;
  templates: TemplateListRow[];
  /** Caller-resolved button label — the connected template's
   *  shortTitle when there's a single option, generic otherwise. */
  label: string;
}): ReactElement {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onMouse = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onMouse);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouse);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  async function createChild(row: TemplateListRow, engine: DocumentEngine = defaultEngine(row)): Promise<void> {
    setErr(null);
    setCreating(row.version_id);
    try {
      const r = await tenantApi.contracts.create_from_template({
        template_version_id: row.version_id,
        parent_contract_id: contractId,
        ...(engine === 'ir' ? { engine } : {}),
      });
      navigate(contractEditorPath(r.contract_id, engine));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setCreating(null);
    }
  }

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
        className={
          'inline-flex items-center gap-1 rounded-full border border-dashed px-2.5 py-1 text-xs font-medium transition-colors ' +
          (open
            ? 'border-stone-600 bg-stone-900 text-white'
            : 'border-stone-300 bg-white text-stone-600 hover:border-stone-500 hover:text-stone-900')
        }
      >
        <span aria-hidden="true">+</span>
        <span>{label}</span>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute top-full left-0 z-40 mt-1.5 w-80 rounded-md border border-stone-200 bg-white p-2 shadow-lg shadow-stone-900/5"
        >
          <p className="mb-2 border-b border-stone-100 px-1 pb-2 text-[11px] leading-relaxed text-stone-500">
            Kopplade avtal är egna avtal med egen livscykel — de kan skapas
            när som helst.
          </p>
          <div className="flex flex-col gap-0.5">
            {templates.map((row) => {
              const mod = getTemplateVersion(row.template_key, row.version);
              const meta = mod?.metadata;
              const description = meta?.description ?? row.description ?? '';
              const shortTitle = meta?.shortTitle ?? row.name;
              const signingMode = meta?.signingMode ?? 'scrive';
              const isCreating = creating === row.version_id;
              return (
                <div key={row.version_id} className="flex flex-col">
                <button
                  type="button"
                  role="menuitem"
                  disabled={isCreating}
                  onClick={() => void createChild(row)}
                  className="group flex w-full flex-col gap-0.5 rounded px-2 py-2 text-left transition-colors hover:bg-stone-50 disabled:cursor-progress disabled:opacity-60"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-stone-900">
                      {shortTitle}
                    </span>
                    <span
                      className={
                        'shrink-0 rounded px-1.5 py-0.5 text-[10px] tracking-wider uppercase ' +
                        (signingMode === 'manual_delivery'
                          ? 'bg-amber-100 text-amber-900'
                          : 'bg-stone-100 text-stone-600')
                      }
                      title={
                        signingMode === 'manual_delivery'
                          ? 'Signeras av en part och skickas separat (inte Scrive).'
                          : 'Signeras via Scrive.'
                      }
                    >
                      {signingMode === 'manual_delivery' ? 'Post' : 'Scrive'}
                    </span>
                  </div>
                  {description && (
                    <span className="line-clamp-2 text-[11px] leading-relaxed text-stone-600">
                      {description}
                    </span>
                  )}
                  {isCreating && (
                    <span className="text-[11px] text-stone-500">Skapar…</span>
                  )}
                </button>
                {row.ir_available && (
                  <div className="px-2 pb-1.5">
                    <LegacyCreateLink disabled={isCreating} onClick={() => void createChild(row, 'legacy')} />
                  </div>
                )}
                </div>
              );
            })}
          </div>
          {err && (
            <div className="mt-2 rounded border border-red-300 bg-red-50 p-1.5 text-[11px] text-red-700">
              {err}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
